import { useState } from "react";
import type { FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";
import Reveal from "@/components/base/Reveal";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8u2cgkk00l6eltrmpg";

const aims = [
  {
    icon: "ri-bookmark-3-line",
    title: "资料系统整理",
    desc: "把散落在各处的作品信息统一汇总，按类型、年份清晰归档，方便一次查全。",
  },
  {
    icon: "ri-search-eye-line",
    title: "查询快速直接",
    desc: "筛选与分页代替无限滚动，让影迷与查询用户用最少的操作找到想看的内容。",
  },
  {
    icon: "ri-refresh-line",
    title: "动态持续更新",
    desc: "持续跟进新作发布、拍摄花絮与活动资讯，让关注奥田咲的人第一时间了解进展。",
  },
];

const scopeItems = [
  "奥田咲个人基本资料：姓名、生日、出生地、身高、事务所等。",
  "影视作品档案：电影、电视剧、短片，含导演、角色、片长与简介。",
  "明星动态资讯：新作发布、拍摄花絮、活动出席、媒体访谈与奖项信息。",
  "代表作品导航：从个人资料页可直接跳转到对应作品详情。",
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@okuda-saki.example.com" },
  { icon: "ri-time-line", label: "回复时间", value: "工作日 3 个工作日内" },
  { icon: "ri-map-pin-line", label: "站点运营", value: "线上资料整理团队" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    setStatus("loading");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && /spam/i.test(serverMsg);

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>关于本站与联系方式 - 奥田咲影迷门户</title>
      <meta
        name="description"
        content="了解奥田咲影迷门户的站点宗旨、内容范围与合规声明，并通过联系表单提交资料补充或反馈意见。"
      />
      <meta name="keywords" content="奥田咲,关于本站,联系方式,影迷门户" />

      <SiteHeader />
      <PageHeader
        eyebrow="ABOUT & CONTACT"
        title="关于本站"
        description="本站是一个面向影迷与影视内容查询用户的资料整理站点，专注于奥田咲个人资料、影视作品与动态资讯的清晰呈现。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于" }]}
      />

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-7xl mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aims.map((aim) => (
                <div
                  key={aim.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${aim.icon} text-xl`}></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    {aim.title}
                  </h2>
                  <p className="mt-2.5 text-sm text-foreground-600 leading-7">{aim.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                    内容范围
                  </h2>
                  <ul className="mt-5 space-y-3.5">
                    {scopeItems.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-line text-primary-600 text-lg mt-0.5 shrink-0"></i>
                        <span className="text-sm text-foreground-700 leading-7">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={80}>
                <div className="mt-5 p-6 md:p-8 rounded-lg bg-secondary-50 border border-secondary-200">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-shield-check-line text-secondary-700"></i>
                    合规声明
                  </h2>
                  <p className="mt-4 text-sm text-foreground-700 leading-8">
                    本站为影迷向的资料整理站点，与艺人本人及其所属机构<b className="font-semibold">无任何官方关联</b>。站内所有人物资料、作品信息与动态资讯均为演示数据，仅用于展示站点功能，不代表真实信息。
                  </p>
                  <p className="mt-3 text-sm text-foreground-700 leading-8">
                    站内不使用任何受版权保护的影视海报或剧照，所有配图均为示意性插图。如权利人认为相关内容需要调整，可通过下方表单与我们联系。
                  </p>
                </div>
              </Reveal>

              <Reveal delay={120}>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {contacts.map((c) => (
                    <div
                      key={c.label}
                      className="p-4 rounded-lg bg-background-50 border border-background-200"
                    >
                      <i className={`${c.icon} text-xl text-primary-600`}></i>
                      <p className="mt-3 text-xs text-foreground-500">{c.label}</p>
                      <p className="mt-1 text-sm font-medium text-foreground-900 break-words">
                        {c.value}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={60}>
                <div className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    联系与反馈
                  </h2>
                  <p className="mt-3 text-sm text-foreground-600 leading-7">
                    如果你发现资料有误、希望补充作品信息，或者有任何建议，欢迎通过下面的表单告诉我们。
                  </p>

                  <form
                    id="okuda-contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="mt-6 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                          你的称呼
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          placeholder="请输入称呼"
                          className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-2">
                        反馈类型
                      </label>
                      <div className="relative">
                        <select
                          id="contact-subject"
                          name="subject"
                          defaultValue="资料补充"
                          className="w-full appearance-none px-4 py-3 pr-10 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                        >
                          <option value="资料补充">资料补充</option>
                          <option value="作品信息纠错">作品信息纠错</option>
                          <option value="内容建议">内容建议</option>
                          <option value="版权相关">版权相关</option>
                          <option value="其他">其他</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请在这里填写你的内容（最多 500 字）"
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="field-extra-note"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {status === "loading" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-lg"></i>
                          提交中…
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          提交反馈
                        </>
                      )}
                    </button>

                    {status === "success" && (
                      <div className="flex items-start gap-3 p-4 rounded-md bg-secondary-50 border border-secondary-200">
                        <i className="ri-checkbox-circle-fill text-secondary-600 text-lg mt-0.5"></i>
                        <p className="text-sm text-foreground-800 leading-7">
                          感谢你的反馈！我们已收到你的留言，会尽快查看处理。
                        </p>
                      </div>
                    )}

                    {status === "error" && (
                      <div className="flex items-start gap-3 p-4 rounded-md bg-accent-50 border border-accent-200">
                        <i className="ri-error-warning-line text-accent-700 text-lg mt-0.5"></i>
                        <p className="text-sm text-foreground-800 leading-7">
                          {formError || "提交失败，请稍后重试。"}
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}